import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { PlusOutlined, SearchOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Skeleton, Empty } from "antd";
import ContactItem from "./ContactItem";
import { LangContext, UiTextsContext, UserDataContext } from "../../../contexts/Contexts";
import { searchFriendRepository } from "../../../../infrastructure/services/ChatService";
import { getChatCopy } from "../chatCopy";
import "./ChatRightAside.css";

const FILTERS = [
  { key: "all", copyKey: "all" },
  { key: "unread", copyKey: "unread" },
  { key: "archived", copyKey: "archived" },
];

const normalizeName = (c) =>
  (c.name ?? c.nombre ?? c.nombres ?? "").toString();

const normalizeId = (c) =>
  String(c.id ?? c.idUsuario ?? c.idUsuarioOrigen ?? Math.random());

const ChatRightAside = ({
  contacts = [],
  counters = {},
  activeContactId,
  onSelectContact,
  onStartNewMessage,
  onArchiveChat,
  onDeleteChat,
  activeFilter = "all",
  onFilterChange,
  loading = false,
  isRefreshing = false,
  variant = "default",
}) => {
  const { texts } = useContext(UiTextsContext);
  const { lang } = useContext(LangContext);
  const { userData } = useContext(UserDataContext);
  const copy = useMemo(() => getChatCopy(lang, texts), [lang, texts]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [composerResults, setComposerResults] = useState([]);
  const [isSearchingFriends, setIsSearchingFriends] = useState(false);
  const searchInputRef = useRef(null);
  const isCompact = variant === "compact";

  const filteredContacts = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return (contacts ?? []).filter((c) => {
      return normalizeName(c).toLowerCase().includes(term);
    });
  }, [searchTerm, contacts]);

  const handleNewMessage = () => {
    setSearchTerm("");
    setComposerResults([]);
    setIsComposerOpen(true);
    onStartNewMessage?.();
  };

  const handleCloseComposer = () => {
    setSearchTerm("");
    setComposerResults([]);
    setIsSearchingFriends(false);
    setIsComposerOpen(false);
  };

  const handleSelect = (contact) => {
    onSelectContact?.(contact);
    setIsComposerOpen(false);
  };

  const composerPrimary = composerResults.slice(0, 2);
  const composerRecent = composerResults.slice(2);

  useEffect(() => {
    if (isComposerOpen) {
      searchInputRef.current?.focus();
    }
  }, [isComposerOpen]);

  useEffect(() => {
    if (!isComposerOpen) {
      return undefined;
    }

    const criteria = searchTerm.trim();

    if (!criteria || !userData?.idUsuario) {
      setComposerResults([]);
      setIsSearchingFriends(false);
      return undefined;
    }

    let isMounted = true;

    const timeoutId = setTimeout(async () => {
      setIsSearchingFriends(true);

      try {
        const results = await searchFriendRepository({
          idUsuario: userData.idUsuario,
          criteria,
        });

        const normalizedResults = (results ?? []).map((user) => ({
          ...user,
          id: user.id ?? user.idUsuario,
          avatar: user.fotoPerfilUrl ?? null,
          fotoPerfilUrl: user.fotoPerfilUrl ?? null,
        }));

        if (!isMounted) {
          return;
        }

        setComposerResults(normalizedResults);
      } catch (error) {
        if (isMounted) {
          console.error("Error searching friends:", error);
          setComposerResults([]);
        }
      } finally {
        if (isMounted) {
          setIsSearchingFriends(false);
        }
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [isComposerOpen, searchTerm, userData?.idUsuario]);

  const renderContactList = (list, mode = "default") => (
    <ul className={`chat-right-aside__contact-list ${isCompact ? "chat-right-aside__contact-list--compact" : ""}`}>
      {list.map((contact) => {
        const contactId = normalizeId(contact);
        const menuItems =
          mode === "composer"
            ? []
            : [
                {
                  key: "archive",
                  label:
                    activeFilter === "archived"
                      ? copy.unarchiveChat
                      : copy.archiveChat,
                },
                {
                  key: "delete",
                  label: copy.deleteChat,
                  danger: true,
                },
              
              ];

        return (
          <li
            key={`${mode}-${contactId}`}
            className="contact-link"
            onClick={() => handleSelect(contact)}
          >
            <ContactItem
              avatar={contact.fotoPerfilUrl ?? contact.avatar}
              name={normalizeName(contact)}
              lastMessage={contact.mensaje ?? contact.lastMessage ?? contact.nickName}
              timestamp={contact.fechaEnvio ?? contact.timestamp}
              unreadCount={mode === "composer" ? 0 : contact.unreadCount}
              isUnread={mode === "composer" ? false : (contact.unreadCount || 0) > 0}
              isActive={mode === "composer" ? false : String(activeContactId ?? "") === contactId}
              menuItems={menuItems}
              onMenuClick={(actionKey) => {
                if (actionKey === "archive") {
                  onArchiveChat?.(contact);
                }

                if (actionKey === "delete") {
                  onDeleteChat?.(contact);
                }
              }}
            />
          </li>
        );
      })}
    </ul>
  );

  if (loading) {
    const skeletonCount = isCompact ? 6 : 8;
    return (
      <aside className={`chat-right-aside ${isCompact ? "chat-right-aside--compact" : ""}`}>
        <div className="chat-right-aside__header">
          <div>
            <h2>{copy.messages}</h2>
          </div>
          {/* <button
            className="chat-right-aside__new-message-btn"
            title={copy.newMessage}
            disabled
            type="button"
          >
            <PlusOutlined />
          </button> */}
        </div>

        <div className="chat-right-aside__search">
          <div className="chat-right-aside__search-shell">
            <SearchOutlined className="chat-right-aside__search-icon" />
            <input
              type="text"
              placeholder={copy.searchNomad}
              value={searchTerm}
              disabled
              aria-disabled
            />
          </div>
        </div>

        <div className="chat-right-aside__filters">
          {FILTERS.map((filter) => (
            <button key={filter.key} className="chat-filter-chip" disabled type="button">
              {copy[filter.copyKey]}
            </button>
          ))}
        </div>

        <ul className="chat-right-aside__contact-list">
          {Array.from({ length: skeletonCount }).map((_, idx) => (
            <li key={`skeleton-${idx}`} className="contact-link">
              <div className="skeleton-row">
                <Skeleton.Avatar active shape="circle" size="large" />
                <div className="skeleton-text">
                  <Skeleton.Input active size="small" style={{ width: 160 }} />
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: 120, marginTop: 6 }}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </aside>
    );
  }

  if (isComposerOpen) {
    return (
      <aside className={`chat-right-aside ${isCompact ? "chat-right-aside--compact" : ""}`}>
        <div className="chat-right-aside__header chat-right-aside__header--composer">
          <div>
            <h2>{copy.friendsNewChat}</h2>
          </div>
          <button
            className="chat-right-aside__back-btn"
            title={copy.backToMessages}
            type="button"
            onClick={handleCloseComposer}
          >
            <ArrowLeftOutlined />
          </button>
        </div>

        <div className="chat-right-aside__search chat-right-aside__search--composer">
          <div className="chat-right-aside__search-shell">
            <SearchOutlined className="chat-right-aside__search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={copy.searchFriend}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {!searchTerm.trim() ? (
          <div className="chat-right-aside__empty-filter">
            <p>{copy.searchForFriend}</p>
            <span>{copy.searchForFriendHint}</span>
          </div>
        ) : isSearchingFriends ? (
          <ul className="chat-right-aside__contact-list">
            {Array.from({ length: 3 }).map((_, idx) => (
              <li key={`composer-skeleton-${idx}`} className="contact-link">
                <div className="skeleton-row">
                  <Skeleton.Avatar active shape="circle" size="large" />
                  <div className="skeleton-text">
                    <Skeleton.Input active size="small" style={{ width: 160 }} />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: 120, marginTop: 6 }}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : !composerResults.length ? (
          <div className="chat-right-aside__empty-filter">
            <p>{copy.noMatches}</p>
            <span>{copy.tryAnotherName}</span>
          </div>
        ) : (
          <div className="chat-right-aside__composer-list">
            {composerPrimary.length ? renderContactList(composerPrimary, "composer") : null}
            {composerRecent.length ? (
              <>
                <p className="chat-right-aside__section-label">{copy.recent}</p>
                {renderContactList(composerRecent, "composer")}
              </>
            ) : null}
          </div>
        )}
      </aside>
    );
  }

  return (
    <aside
      className={`chat-right-aside ${isCompact ? "chat-right-aside--compact" : ""}`}
      aria-busy={isRefreshing}
    >
      <div className="chat-right-aside__header">
        <div>
          <h2>{copy.messages}</h2>
        </div>
        {/* <button
          className="chat-right-aside__new-message-btn"
          title={copy.newMessage}
          type="button"
          onClick={handleNewMessage}
        >
          <PlusOutlined />
        </button> */}
      </div>

      <div className="chat-right-aside__search">
        <div className="chat-right-aside__search-shell">
          <SearchOutlined className="chat-right-aside__search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={copy.searchNomad}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="chat-right-aside__filters" role="tablist" aria-label={copy.messages}>
        {FILTERS.map((filter) => (
          <button
            key={filter.key}
            type="button"
            className={`chat-filter-chip ${activeFilter === filter.key ? "chat-filter-chip--active" : ""}`}
            onClick={() => onFilterChange?.(filter.key)}
          >
            <span>{copy[filter.copyKey]}</span>
            <span className="chat-filter-chip__count">{counters[filter.key] ?? 0}</span>
          </button>
        ))}
      </div>

    

      {!contacts.length && !isRefreshing ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={copy.noMessages}
          style={{ marginTop: 24 }}
        />
      ) : !filteredContacts.length && !isRefreshing ? (
        <div className="chat-right-aside__empty-filter">
          <p>{copy.noChatsInView}</p>
          <span>{copy.adjustFilter}</span>
        </div>
      ) : filteredContacts.length ? (
        renderContactList(filteredContacts)
      ) : null}
    </aside>
  );
};

export default ChatRightAside;
